import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import { alpha } from '@mui/material/styles';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import Typography from '@mui/material/Typography';
import { useSelectedBusinessUnit } from 'contexts/SidebarContext';
import type React from 'react';
import { useEffect, useMemo, useState } from 'react';
import { useGetMetaConnectionsForBusinessUnitQuery } from 'services/api/relifMeta.api';
import { NAVY, RADIUS_CARD, SURFACE_CONTAINER_LOW, YELLOW } from 'styles/tokens';

import AdsTab from './tabs/AdsTab';
import ConnectionTab from './tabs/ConnectionTab';
import FormsTab from './tabs/FormsTab';
import PixelTab from './tabs/PixelTab';

type TabKey = 'connection' | 'ads' | 'pixel' | 'forms';

const TABS: Array<{ key: TabKey; label: string }> = [
  { key: 'connection', label: 'Conexión' },
  { key: 'ads', label: 'Cuentas' },
  { key: 'pixel', label: 'Píxel & Eventos' },
  { key: 'forms', label: 'Formularios' },
];

const MetaConfig: React.FC = () => {
  const { selectedBusinessUnitId } = useSelectedBusinessUnit();
  const [activeTab, setActiveTab] = useState<TabKey>('connection');

  const buId = selectedBusinessUnitId ?? 0;
  const skipQuery = !selectedBusinessUnitId;
  const { data, isLoading } = useGetMetaConnectionsForBusinessUnitQuery(buId, { skip: skipQuery });
  const connections = useMemo(() => data?.connections ?? [], [data]);
  const [selectedConnectionId, setSelectedConnectionId] = useState<number | null>(null);
  const connection = useMemo(() => {
    if (connections.length === 0) return null;
    if (selectedConnectionId === null) return connections[0];
    return connections.find((c) => c.id === selectedConnectionId) ?? connections[0];
  }, [connections, selectedConnectionId]);

  useEffect(() => {
    setSelectedConnectionId(null);
  }, [selectedBusinessUnitId]);

  return (
    <Container maxWidth="lg" sx={{ pt: 3, pb: 8 }}>
      {!selectedBusinessUnitId ? (
        <BUSelectionEmpty />
      ) : (
        <>
          <Box sx={{ mb: 4 }}>
            <Tabs
              value={activeTab}
              onChange={(_, value) => setActiveTab(value as TabKey)}
              variant="scrollable"
              scrollButtons={false}
              slotProps={{
                indicator: {
                  sx: {
                    height: 3,
                    borderRadius: 999,
                    backgroundColor: YELLOW,
                  },
                },
              }}
              sx={{
                minHeight: 44,
                '& .MuiTabs-flexContainer': { gap: 1 },
                '& .MuiTab-root': {
                  fontFamily: 'Jost, sans-serif',
                  fontWeight: 600,
                  fontSize: '0.9375rem',
                  textTransform: 'none',
                  color: alpha(NAVY, 0.55),
                  letterSpacing: '0.005em',
                  minHeight: 44,
                  px: 1.5,
                  transition: 'color 0.18s ease',
                  '&:hover': { color: alpha(NAVY, 0.85) },
                },
                '& .Mui-selected': { color: `${NAVY} !important` },
              }}
            >
              {TABS.map((tab) => (
                <Tab key={tab.key} value={tab.key} label={tab.label} disableRipple />
              ))}
            </Tabs>
            <Box sx={{ height: 1, backgroundColor: alpha(NAVY, 0.06) }} />
          </Box>

          {activeTab === 'connection' && (
            <ConnectionTab
              businessUnitId={selectedBusinessUnitId}
              connection={connection}
              connections={connections}
              isLoading={isLoading}
              selectedConnectionId={connection?.id ?? null}
              onSelectConnection={setSelectedConnectionId}
              onJumpToTab={(key) => setActiveTab(key)}
            />
          )}
          {activeTab === 'ads' && (
            <AdsTab
              businessUnitId={selectedBusinessUnitId}
              connection={connection}
              isLoading={isLoading}
              onSwitchToConnection={() => setActiveTab('connection')}
            />
          )}
          {activeTab === 'pixel' && (
            <PixelTab
              businessUnitId={selectedBusinessUnitId}
              connection={connection}
              isLoading={isLoading}
              onSwitchToConnection={() => setActiveTab('connection')}
            />
          )}
          {activeTab === 'forms' && (
            <FormsTab
              businessUnitId={selectedBusinessUnitId}
              connection={connection}
              isLoading={isLoading}
              onSwitchToConnection={() => setActiveTab('connection')}
            />
          )}
        </>
      )}
    </Container>
  );
};

const BUSelectionEmpty: React.FC = () => (
  <Box
    sx={{
      backgroundColor: SURFACE_CONTAINER_LOW,
      borderRadius: RADIUS_CARD,
      py: { xs: 8, md: 12 },
      px: 4,
      textAlign: 'center',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: 1.5,
    }}
  >
    <Typography
      sx={{
        fontFamily: 'Jost, sans-serif',
        fontSize: '0.6875rem',
        fontWeight: 600,
        letterSpacing: '0.12em',
        textTransform: 'uppercase',
        color: alpha(NAVY, 0.5),
      }}
    >
      Sin BU seleccionada
    </Typography>
    <Typography
      sx={{
        fontFamily: 'Jost, sans-serif',
        fontSize: { xs: '1.5rem', md: '2rem' },
        fontWeight: 700,
        color: NAVY,
        letterSpacing: '-0.01em',
        maxWidth: 480,
        lineHeight: 1.15,
      }}
    >
      Elige una unidad de negocio en la barra lateral.
    </Typography>
    <Typography sx={{ fontFamily: 'Jost, sans-serif', fontSize: '0.95rem', color: alpha(NAVY, 0.55), maxWidth: 460 }}>
      Cada BU tiene su propia conexión con Meta, cuentas publicitarias, píxel y formularios de leads.
    </Typography>
  </Box>
);

export default MetaConfig;
